'use client';

// Vista ejecutiva del flujo (estilo McKinsey): conclusión arriba, flujo simple con
// el cuello de botella marcado, tus decisiones en una línea, detalle plegado.
import { useCallback, useEffect, useState } from 'react';

interface Gate {
  id: number;
  gate: 'objetivos' | 'mensajes' | 'terminos' | 'escalar';
  question: string;
  proposal: string | null;
  created_at: string;
  partner: string | null;
}
interface Activ {
  partner: string;
  country: string;
  mechanism: string | null;
  push_channel: string | null;
  next_push_at: string | null;
  codes: { code: string; total: number; ult_3_meses: number }[];
  canjes_3m: number;
  flags: string[];
}
interface Etapa {
  id: string;
  label: string;
  agente: string;
  fase: number;
  n: number;
  unidad: string;
  gate?: Gate['gate'];
  gate_pendientes: number;
}
interface Flow {
  etapas: Etapa[];
  ultima_corrida: { started_at: string; summary: string } | null;
  vigia: { canal: string }[];
  verificador_total: number;
  sin_decisor: Record<string, number>;
  activador: Activ[];
  medidor: { country: string; dias: number }[];
  compuertas: Gate[];
  playbooks: {
    key: string;
    segment: string;
    action: string;
    channel: string;
    description: string;
    template: string;
    status: 'propuesta' | 'aprobada' | 'pausada';
    enviadas_7d: number;
  }[];
  cola: { id: number; playbook_key: string; channel: string; to_name: string; partner: string | null }[];
  salio_solo: { id: number; playbook_key: string; channel: string; to_name: string; sent_at: string; partner: string | null }[];
  por_ejecutar: { id: number; gate: Gate['gate']; question: string; decision: string; partner: string | null }[];
}

const NAVY = '#051C2C';
const ACCENT = '#2251FF';

const GATE_LABEL: Record<Gate['gate'], string> = {
  objetivos: 'Objetivos',
  mensajes: 'Mensaje',
  terminos: 'Términos',
  escalar: 'Escalar o matar',
};

function ago(iso: string) {
  const t = new Date(iso.replace(' ', 'T') + (iso.includes('T') ? '' : 'Z')).getTime();
  const h = Math.round((Date.now() - t) / 3600000);
  if (h < 1) return 'hace minutos';
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}

// La "so-what" de la página: una frase que dice dónde está el problema.
function headline(f: Flow): { title: string; bottleneck: string } {
  const signed = f.activador.length;
  const dead = f.activador.filter((a) => a.canjes_3m === 0).length;
  if (signed && dead / signed >= 0.5) {
    return {
      title: `${dead} de ${signed} alianzas firmadas no generan pedidos: el cuello de botella es activar, no cerrar.`,
      bottleneck: 'activar',
    };
  }
  if (f.verificador_total > 30) {
    return { title: `${f.verificador_total} contactos esperan verificación antes de escribirles.`, bottleneck: 'calificar' };
  }
  return { title: `${f.compuertas.length} decisiones tuyas mueven el flujo esta semana.`, bottleneck: '' };
}

export default function FlowPanel() {
  const [f, setF] = useState<Flow | null>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [answer, setAnswer] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [openRule, setOpenRule] = useState<string | null>(null);

  const setRule = async (key: string, set: 'aprobada' | 'pausada') => {
    await fetch('/api/flow', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playbook: key, set }),
    });
    load();
  };

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/flow');
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setF(j);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id: number, decision: string | null) => {
    await fetch('/api/flow', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(decision === null ? { id, drop: true } : { id, decision }),
    });
    setOpen(null);
    setAnswer('');
    load();
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!f) return <p className="text-sm text-gray-400">Cargando…</p>;

  const h = headline(f);

  return (
    <div className="max-w-5xl" style={{ color: NAVY }}>
      {/* 1 · La conclusión */}
      <p className="text-[11px] uppercase tracking-[0.14em] text-gray-400 mb-2">
        Alianzas LATAM · {f.ultima_corrida ? `actualizado ${ago(f.ultima_corrida.started_at)}` : 'sin corridas'}
      </p>
      <h2 className="text-2xl md:text-[28px] font-semibold leading-snug mb-8" style={{ fontFamily: 'Georgia, serif' }}>
        {h.title}
      </h2>

      {/* 2 · El flujo */}
      <div className="grid grid-cols-6 border-t border-b border-gray-200 mb-2">
        {f.etapas.map((e, i) => {
          const hot = e.id === h.bottleneck;
          return (
            <div key={e.id} className={`py-4 px-3 ${i ? 'border-l border-gray-200' : ''} ${hot ? 'bg-[#2251FF]/[0.06]' : ''}`}>
              <p className="text-[11px] text-gray-500">{e.label}</p>
              <p className="text-3xl font-light mt-1" style={{ color: hot ? ACCENT : NAVY }}>
                {e.n}
              </p>
              <p className="text-[11px] text-gray-500 leading-tight mt-0.5">{e.unidad}</p>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-400 mb-10">
        Descubrir → Calificar → Contactar → Convertir → Activar → Medir. Los agentes hacen cada etapa; tú decides en 4 puntos.
      </p>

      {/* 3 · Tus decisiones */}
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className="text-base font-semibold">Excepciones</h3>
        <span className="text-sm text-gray-400">{f.compuertas.length} · solo esto necesita tu OK; lo demás sale solo con tus reglas</span>
      </div>
      {f.compuertas.length === 0 ? (
        <p className="text-sm text-gray-400 mb-10">Ninguna excepción. Todo lo demás corre con tus reglas.</p>
      ) : (
        <ol className="border-t border-gray-200 mb-10">
          {f.compuertas.map((g, i) => (
            <li key={g.id} className="border-b border-gray-200">
              <button
                onClick={() => {
                  setOpen(open === g.id ? null : g.id);
                  setAnswer('');
                }}
                className="w-full text-left py-3 flex items-baseline gap-4 hover:bg-gray-50"
              >
                <span className="text-sm text-gray-400 w-5">{i + 1}</span>
                <span className="text-[11px] uppercase tracking-wide text-gray-400 w-28 flex-shrink-0">{GATE_LABEL[g.gate]}</span>
                <span className="text-sm flex-1">
                  {g.partner && <b className="font-semibold">{g.partner}. </b>}
                  {g.question.length > 120 ? g.question.slice(0, 118) + '…' : g.question}
                </span>
                <span className="text-xs" style={{ color: ACCENT }}>
                  {open === g.id ? 'cerrar' : 'ver'}
                </span>
              </button>
              {open === g.id && (
                <div className="pl-9 pr-2 pb-4">
                  {g.proposal && (
                    <div className="text-sm text-gray-700 whitespace-pre-line border-l-2 pl-4 mb-3" style={{ borderColor: ACCENT }}>
                      {g.proposal}
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => resolve(g.id, 'Aprobado')}
                      className="text-sm px-4 py-1.5 text-white"
                      style={{ backgroundColor: NAVY }}
                    >
                      {g.gate === 'mensajes' ? 'Aprobar para enviar' : 'Aprobar'}
                    </button>
                    <input
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && answer && resolve(g.id, answer)}
                      placeholder="o escribe otra decisión y Enter"
                      className="text-sm px-3 py-1.5 border border-gray-300 flex-1 focus:outline-none focus:border-gray-500"
                    />
                    <button onClick={() => resolve(g.id, null)} className="text-xs text-gray-400 hover:text-gray-600 px-2">
                      descartar
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {/* Reglas por segmento: se aprueban una vez */}
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className="text-base font-semibold">Reglas por segmento</h3>
        <span className="text-sm text-gray-400">
          apruebas una vez · lo que calza sale solo (excepto gerentes generales, términos y ultimátums)
        </span>
      </div>
      <ul className="border-t border-gray-200 mb-10">
        {(f.playbooks ?? []).map((pb) => (
          <li key={pb.key} className="border-b border-gray-200">
            <div className="py-3 flex items-baseline gap-4">
              <span
                className={`text-[11px] uppercase tracking-wide w-24 flex-shrink-0 ${
                  pb.status === 'aprobada' ? 'text-green-700' : pb.status === 'pausada' ? 'text-gray-400' : 'text-amber-700'
                }`}
              >
                {pb.status === 'aprobada' ? '● activa' : pb.status === 'pausada' ? '○ pausada' : '◐ por aprobar'}
              </span>
              <button onClick={() => setOpenRule(openRule === pb.key ? null : pb.key)} className="text-sm flex-1 text-left hover:underline">
                <b className="font-semibold capitalize">{pb.segment}.</b> {pb.description}
                <span className="text-gray-400"> · {pb.channel}</span>
              </button>
              {pb.enviadas_7d > 0 && <span className="text-xs text-gray-500">{pb.enviadas_7d} esta semana</span>}
              {pb.status === 'aprobada' ? (
                <button onClick={() => setRule(pb.key, 'pausada')} className="text-xs text-gray-400 hover:text-gray-600">
                  pausar
                </button>
              ) : (
                <button onClick={() => setRule(pb.key, 'aprobada')} className="text-xs px-3 py-1 text-white" style={{ backgroundColor: NAVY }}>
                  Aprobar regla
                </button>
              )}
            </div>
            {openRule === pb.key && (
              <div className="text-sm text-gray-700 whitespace-pre-line border-l-2 pl-4 ml-28 mb-3" style={{ borderColor: ACCENT }}>
                {pb.template}
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Lo que salió solo */}
      {((f.cola?.length ?? 0) > 0 || (f.salio_solo?.length ?? 0) > 0) && (
        <div className="mb-10">
          <div className="flex items-baseline gap-3 mb-3">
            <h3 className="text-base font-semibold">Salió solo</h3>
            <span className="text-sm text-gray-400">últimos 7 días · {f.cola?.length ?? 0} en cola para la próxima corrida</span>
          </div>
          <ul className="border-t border-gray-200">
            {[...(f.cola ?? []).map((c) => ({ ...c, when: 'en cola' })), ...(f.salio_solo ?? []).map((c) => ({ ...c, when: ago(c.sent_at) }))].map(
              (c) => (
                <li key={c.id} className="border-b border-gray-200 py-2 flex items-baseline gap-4 text-sm">
                  <span className="text-[11px] uppercase tracking-wide text-gray-400 w-24 flex-shrink-0">{c.channel}</span>
                  <span className="flex-1">
                    {c.partner && <b className="font-semibold">{c.partner}. </b>}
                    {c.to_name} <span className="text-gray-400">· {c.playbook_key}</span>
                  </span>
                  <span className="text-xs text-gray-500">{c.when}</span>
                </li>
              ),
            )}
          </ul>
        </div>
      )}

      {f.por_ejecutar?.length > 0 && (
        <div className="mb-10">
          <div className="flex items-baseline gap-3 mb-3">
            <h3 className="text-base font-semibold">Aprobado, falta ejecutar</h3>
            <span className="text-sm text-gray-400">sale cuando le pides a Claude “/flujo” (la página no envía)</span>
          </div>
          <ul className="border-t border-gray-200">
            {f.por_ejecutar.map((d) => (
              <li key={d.id} className="border-b border-gray-200 py-2.5 flex items-baseline gap-4 text-sm">
                <span className="text-[11px] uppercase tracking-wide text-gray-400 w-28 flex-shrink-0">{GATE_LABEL[d.gate]}</span>
                <span className="flex-1">
                  {d.partner && <b className="font-semibold">{d.partner}. </b>}
                  {d.question}
                </span>
                <span className="text-xs text-gray-500">✓ {d.decision}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 4 · Detalle (plegado) */}
      <button onClick={() => setShowDetail(!showDetail)} className="text-sm mb-4" style={{ color: ACCENT }}>
        {showDetail ? '− Ocultar detalle' : '+ Ver detalle por alianza'}
      </button>
      {showDetail && (
        <table className="w-full text-sm border-t border-gray-200 mb-6">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-gray-400 text-left">
              <th className="font-normal py-2">Alianza firmada</th>
              <th className="font-normal py-2">Cómo llega</th>
              <th className="font-normal py-2">Próximo empuje</th>
              <th className="font-normal py-2 text-right">Pedidos 3 meses</th>
            </tr>
          </thead>
          <tbody>
            {f.activador.map((a) => (
              <tr key={a.partner + a.country} className="border-t border-gray-100">
                <td className="py-2">
                  {a.partner} <span className="text-gray-400 text-xs">{a.country}</span>
                </td>
                <td className="py-2 text-gray-600">{a.push_channel ? `${a.mechanism} · ${a.push_channel.split('(')[0].slice(0, 60)}` : '—'}</td>
                <td className="py-2 text-gray-600">{a.next_push_at ?? '—'}</td>
                <td className="py-2 text-right" style={{ color: a.canjes_3m ? NAVY : '#C0392B' }}>
                  {a.canjes_3m}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="text-[11px] text-gray-400">Lo que calza con una regla activa sale solo en cada corrida de “/flujo” (Claude envía desde tu Chrome). Las excepciones esperan tu OK aquí.</p>
    </div>
  );
}
